import { createSelector } from '@ngrx/store';

import { cartDataSelector } from '@fcom/booking/store/selectors/cart.selector';
import { FinnairPassengerServiceSelectionItem } from '@fcom/dapi/api/models/finnair-passenger-service-selection-item';
import { FinnairServiceSegmentItem } from '@fcom/dapi/api/models/finnair-service-segment-item';

import { orderDataSelector } from '../../../../store/selectors/order.selector';

const boundServicesSelector = createSelector(
  cartDataSelector,
  orderDataSelector,
  (cartData, orderData) => orderData?.services || cartData?.services
);

const unpaidServiceBoundItemsSelector = createSelector(
  boundServicesSelector,
  (services) => services?.unpaid?.flatMap((serviceItem) => serviceItem.bounds || []) || []
);

const includedServiceBoundItemsSelector = createSelector(
  boundServicesSelector,
  (services) => services?.included?.flatMap((serviceItem) => serviceItem.bounds || []) || []
);

const PET_IN_CABIN_VARIANT = 'PETC';

const serviceIsPetcIncompatible = (service: FinnairPassengerServiceSelectionItem) =>
  !!service?.parameters?.incompatibleServiceVariants?.includes(PET_IN_CABIN_VARIANT);

const extractPaxIdsWhichHavePetcIncompatibleSeats = (segment: FinnairServiceSegmentItem) =>
  segment?.passengers
    ?.filter((p) => {
      return p?.services?.some(serviceIsPetcIncompatible);
    })
    .map((p) => p.id) || [];

const extractUniquePassengerIdsAssociatedWithPetcIncompatibleServices = (
  segments: FinnairServiceSegmentItem[]
): string[] => Array.from(new Set(segments?.flatMap(extractPaxIdsWhichHavePetcIncompatibleSeats)));

export const passengersShouldBeShownPetcConfirmationModalSelector = createSelector(
  unpaidServiceBoundItemsSelector,
  (sbis) =>
    sbis.reduce(
      (boundAcc, curBoundItem) => ({
        ...boundAcc,
        [curBoundItem.id]: Array.from(
          new Set([
            ...(boundAcc[curBoundItem.id] || []),
            ...extractUniquePassengerIdsAssociatedWithPetcIncompatibleServices(curBoundItem.segments),
          ])
        ),
      }),
      {}
    )
);

export const passengersShouldBeShownPetcBlockingMessageSelector = createSelector(
  includedServiceBoundItemsSelector,
  (sbis) =>
    sbis.reduce(
      (boundAcc, curBoundItem) => ({
        ...boundAcc,
        [curBoundItem.id]: Array.from(
          new Set([
            ...(boundAcc[curBoundItem.id] || []),
            ...extractUniquePassengerIdsAssociatedWithPetcIncompatibleServices(curBoundItem.segments),
          ])
        ),
      }),
      {}
    )
);

export const flightsAndPaxWhichShouldHaveSeatsClearedIfAddingPetcSelector = createSelector(
  unpaidServiceBoundItemsSelector,
  (unpaidServiceBoundItems) =>
    unpaidServiceBoundItems.reduce(
      (boundAcc, curBoundItem) => ({
        ...boundAcc,
        [curBoundItem.id]: Array.from(
          new Set([
            ...(boundAcc[curBoundItem.id] || []),
            ...(curBoundItem.segments?.flatMap((s) =>
              extractPaxIdsWhichHavePetcIncompatibleSeats(s).map((paxId) => [s.id, paxId])
            ) || []),
          ])
        ),
      }),
      {}
    )
);
